import type { RealtimeEvent } from "@island/realtime-core";
import { createChime } from "@island/browser-client";
import { PERMISSIONS } from "@island/auth-core";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import type { ConversationListResponse } from "../api/types.js";
import type { AgentConversationDto } from "../api/types.js";
import { ConversationView } from "../components/ConversationView.js";
import { QueueList, type QueueFilter } from "../components/QueueList.js";
import { useRealtime } from "../hooks/useRealtime.js";
import { useSession } from "../session/SessionContext.js";

/**
 * The dashboard proper: a queue on the left, a conversation on the right.
 *
 * The socket lives here rather than in either child, so switching conversations does
 * not reconnect — see `useRealtime`.
 */
/**
 * How many messages this browser had seen in each conversation.
 *
 * A high-water mark per conversation, keyed by id. Stored rather than held in
 * memory so that a refresh — or a laptop lid closed overnight — does not turn
 * the whole queue unread again, which would make the badges worthless on the
 * one morning they matter.
 */
const SEEN_KEY = "island_dashboard_seen";

function readSeen(): Record<string, number> {
  try {
    const raw = window.localStorage.getItem(SEEN_KEY);
    const parsed: unknown = raw === null ? null : JSON.parse(raw);

    return typeof parsed === "object" && parsed !== null ? (parsed as Record<string, number>) : {};
  } catch {
    // Private browsing, or a value from an older shape. Starting fresh costs
    // one round of badges, not correctness.
    return {};
  }
}

function writeSeen(seen: Record<string, number>): void {
  try {
    window.localStorage.setItem(SEEN_KEY, JSON.stringify(seen));
  } catch {
    // Nothing to do: the marks are a convenience, not state the platform needs.
  }
}

export function QueueView() {
  const { api, signOut, principal, can } = useSession();
  const navigate = useNavigate();
  const { conversationId } = useParams<{ conversationId: string }>();

  /**
   * Everything, not just what is waiting.
   *
   * "Waiting" hides a conversation the moment an agent joins it — including the
   * one they are answering — so a customer replying to a live chat produced no
   * visible change anywhere on the screen. The queue is ordered by activity, so
   * whatever just happened is at the top regardless of its state.
   */
  const [filter, setFilter] = useState<QueueFilter>("all");

  /**
   * How many messages have arrived in each conversation since this agent last
   * had it open.
   *
   * Counted rather than flagged, because "3" tells an agent something "•" does
   * not — the same reason a messaging app shows a number. It is derived from
   * `messageCount`, which every queue response already carries, against a
   * per-conversation high-water mark this browser remembers.
   *
   * Kept in localStorage and not on the server: "unread" is a property of the
   * person looking, and the platform has no concept of which agent has seen
   * what. Two agents watching the same queue each have their own answer, which
   * is correct.
   */
  const [unread, setUnread] = useState<ReadonlyMap<string, number>>(new Map());
  const seenRef = useRef<Record<string, number>>(readSeen());

  /** Conversations ticked for deletion. Cleared whenever the list changes. */
  const [checked, setChecked] = useState<ReadonlySet<string>>(new Set());
  const [deleting, setDeleting] = useState(false);
  const [conversations, setConversations] = useState<readonly AgentConversationDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  /** Bumped to make the open conversation refetch after a live change. */
  const [revision, setRevision] = useState(0);
  /** The latest typing signal, forwarded to the open conversation. */
  const [typingEvent, setTypingEvent] = useState<Extract<RealtimeEvent, { type: "typing" }> | null>(
    null,
  );
  /** The latest presence change, likewise. */
  const [presenceEvent, setPresenceEvent] = useState<Extract<
    RealtimeEvent,
    { type: "presence" }
  > | null>(null);

  /**
   * The new-message sound.
   *
   * Built once and held in a ref: recreating it per render would leak an
   * `AudioContext` on every keystroke, and browsers cap how many a page may have.
   */
  const chime = useRef(
    createChime({
      storageKey: "island_dashboard_muted",
      /*
       * Louder than the shopper's.
       *
       * The two sounds are doing different jobs. A customer hears theirs while
       * looking at the chat they just used; an agent needs to be pulled back to
       * a screen they are not currently on, from across a room, possibly while
       * doing something else. The default 0.12 is a polite notification — too
       * polite for a queue that is somebody's job.
       */
      volume: 0.55,
    }),
  ).current;
  const [muted, setMuted] = useState(chime.muted);

  useEffect(
    () => () => {
      chime.close();
    },
    [chime],
  );

  const realtimeUrl = useMemo(() => {
    const base = import.meta.env.VITE_REALTIME_URL ?? "";

    if (base !== "") return base;

    // Same host by default, upgraded to wss wherever the page itself is secure.
    const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    return `${protocol}//${window.location.host}/realtime`;
  }, []);

  const query = useMemo(() => {
    switch (filter) {
      case "waiting":
        return "?status=awaiting_agent";
      case "mine":
        return "?assignedToMe=true";
      case "unassigned":
        return "?unassignedOnly=true";
      case "live":
        return "?status=agent_handling";
      case "all":
        return "";
    }
  }, [filter]);

  const loadQueue = useCallback(async () => {
    setError(null);

    try {
      const page = await api.get<ConversationListResponse>(`/support/conversations${query}`);
      setConversations(page.conversations);
    } catch {
      setError("The queue could not be loaded.");
    } finally {
      setLoading(false);
    }
  }, [api, query]);

  useEffect(() => {
    setLoading(true);
    void loadQueue();
  }, [loadQueue]);

  /**
   * Coalesce the refetches a burst of events would otherwise cause.
   *
   * Every customer message anywhere in the platform now reaches this browser — that
   * is what makes a new conversation appear without a reload — and each one asked
   * for the whole queue. Two customers typing at once is enough for that to be
   * several identical requests a second, each replacing the list under an agent's
   * cursor.
   *
   * Half a second is below the point at which anyone perceives a delay in a list
   * that is about to reorder anyway, and above the interval between messages in a
   * burst.
   */
  const refetchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const scheduleQueueReload = useCallback(() => {
    if (refetchTimer.current !== null) return;

    refetchTimer.current = setTimeout(() => {
      refetchTimer.current = null;
      void loadQueue();
    }, 500);
  }, [loadQueue]);

  useEffect(
    () => () => {
      if (refetchTimer.current !== null) clearTimeout(refetchTimer.current);
    },
    [],
  );

  /**
   * React to live events.
   *
   * The queue is refetched rather than patched in place. A queue is a *filtered,
   * ordered* view, and an event does not carry enough to know whether a conversation
   * still belongs in the current filter — patching it would leave closed
   * conversations sitting in the "waiting" list.
   */
  const onEvent = useCallback(
    (event: RealtimeEvent) => {
      /**
       * Sound for anything a customer said, anywhere in the queue.
       *
       * Not limited to the conversation on screen: the point is to tell an agent
       * that something needs them, and the one they are already reading is the case
       * that needs it least. Agent and assistant messages are silent — an agent does
       * not need a chime for their own reply.
       */
      if (
        event.type === "message.created" &&
        (event.message.senderType === "customer" || event.message.senderType === "guest")
      ) {
        chime.play();

        /**
         * And reload, so it rises to the top and the badge appears.
         *
         * Previously only `conversation.changed` refetched, so a new message in
         * an existing chat changed nothing an agent could see: no reordering, no
         * badge, just a sound with nothing to look at.
         */
        scheduleQueueReload();
      }

      if (event.type === "conversation.changed") {
        scheduleQueueReload();

        // A conversation arriving in the queue is worth hearing even with no message
        // attached — an escalation is exactly what an agent is waiting for.
        if (event.change === "escalated") chime.play();
      }

      if (event.conversationId !== conversationId) return;

      // Typing never touches the server, so it is forwarded rather than triggering a
      // refetch — reloading a transcript on every keystroke would be absurd.
      if (event.type === "typing") {
        setTypingEvent(event);
        return;
      }

      // Somebody opening or closing a browser tab changes nothing about the
      // transcript, so this forwards without triggering the refetch below.
      if (event.type === "presence") {
        setPresenceEvent(event);
        return;
      }

      setRevision((current) => current + 1);
    },
    [scheduleQueueReload, conversationId, chime],
  );

  /**
   * Badges, recomputed from the queue rather than accumulated from events.
   *
   * Derived state, so a reload, a reconnect or a missed event all converge on
   * the same answer — an accumulated counter drifts the moment one frame is
   * lost, and a support queue is exactly where that would be noticed.
   *
   * The conversation currently open is marked seen as it is read, so an
   * agent's own reply never lights up their own row.
   */
  useEffect(() => {
    const seen = seenRef.current;
    let changed = false;

    if (conversationId !== undefined) {
      const open = conversations.find((c) => c.conversationId === conversationId);

      if (open !== undefined && seen[conversationId] !== open.messageCount) {
        seen[conversationId] = open.messageCount;
        changed = true;
      }
    }

    const next = new Map<string, number>();

    for (const conversation of conversations) {
      if (conversation.conversationId === conversationId) continue;

      const behind = conversation.messageCount - (seen[conversation.conversationId] ?? 0);

      // A conversation this browser has never seen is not "all unread" — it is
      // simply new, and one badge is the honest way to say so.
      if (behind > 0) {
        next.set(
          conversation.conversationId,
          seen[conversation.conversationId] === undefined ? 1 : behind,
        );
      }
    }

    if (changed) writeSeen(seen);
    setUnread(next);
  }, [conversations, conversationId]);

  /**
   * Refetch on a timer, and whenever the tab comes back.
   *
   * The socket only delivers events for conversations this agent has
   * subscribed to — which means the ones they have opened. A brand-new chat
   * from a customer nobody has looked at yet reaches no one, and the queue sat
   * unchanged until somebody pressed refresh. That is the whole complaint, and
   * a poll is the honest fix: the platform has no agent-wide broadcast to
   * listen to.
   *
   * Ten seconds is frequent enough that a waiting customer is noticed and
   * cheap enough to be invisible — one small query per agent.
   */
  useEffect(() => {
    const timer = setInterval(() => {
      if (document.visibilityState === "visible") void loadQueue();
    }, 10_000);

    const onWake = (): void => {
      if (document.visibilityState === "visible") void loadQueue();
    };

    document.addEventListener("visibilitychange", onWake);
    window.addEventListener("focus", onWake);

    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onWake);
      window.removeEventListener("focus", onWake);
    };
  }, [loadQueue]);

  /**
   * Delete everything ticked.
   *
   * One at a time rather than in parallel: this is the most destructive thing
   * the dashboard can do, and a burst of concurrent deletes makes a partial
   * failure impossible to reason about. Sequential is slower and tells the
   * truth — whatever failed is still there, still ticked, still deletable.
   */
  const deleteChecked = useCallback(async () => {
    if (checked.size === 0) return;

    const names = conversations
      .filter((c) => checked.has(c.conversationId))
      .map((c) => c.preChat?.firstName ?? "a customer");

    const preview = names.slice(0, 5).join(", ") + (names.length > 5 ? ", …" : "");

    if (
      !window.confirm(
        `Permanently delete ${String(checked.size)} conversation${checked.size === 1 ? "" : "s"}?\n\n` +
          `${preview}\n\n` +
          "Every transcript, agent note and customer image in them is erased. " +
          "This cannot be undone.",
      )
    ) {
      return;
    }

    setDeleting(true);

    const failed = new Set<string>();

    for (const id of checked) {
      try {
        await api.delete(`/support/conversations/${id}`);
      } catch {
        // Kept ticked, so a second press retries exactly what did not go.
        failed.add(id);
      }
    }

    setDeleting(false);
    setChecked(failed);

    if (failed.size > 0) {
      setError(
        `${String(failed.size)} of ${String(checked.size)} could not be deleted. They are still selected.`,
      );
    }

    // If the open conversation was among them there is nothing left to show.
    if (
      conversationId !== undefined &&
      checked.has(conversationId) &&
      !failed.has(conversationId)
    ) {
      void navigate("/");
    }

    await loadQueue();
  }, [api, checked, conversations, conversationId, loadQueue, navigate]);

  const { client, state } = useRealtime(realtimeUrl, onEvent, () => {
    // The socket was refused, which means the session is no longer good. One
    // refresh attempt is enough to tell whether it can be recovered.
    void api.refreshSession();
  });

  return (
    <div className="app">
      <header className="topbar">
        <span className="brand">
          Island&nbsp;Peptides<span className="brand-long">&nbsp;Support</span>
        </span>

        <span className={`status-dot ${state}`} aria-hidden="true" />
        <span className="small muted" title={`Realtime connection: ${state}`}>
          {state === "open" ? "Live" : state === "reconnecting" ? "Reconnecting…" : state}
        </span>

        <span className="spacer" />

        <button
          className="icon"
          aria-pressed={muted}
          aria-label={muted ? "Turn the sound on" : "Turn the sound off"}
          title={muted ? "Sound is off" : "Sound is on"}
          onClick={() => {
            chime.setMuted(!muted);
            setMuted(!muted);
            // Played on unmute so the agent hears what they just switched on, and so
            // the first real message is not the one refused by the autoplay policy.
            if (muted) chime.play();
          }}
        >
          {muted ? "🔇" : "🔔"}
        </button>

        {/*
          No longer permission-gated. It used to be, correctly, because everything
          behind it needed a permission — but the settings area now holds an agent's
          own account, and locking somebody out of their own password would be the
          opposite of what a permission is for. The administrative tabs inside are
          still hidden from anyone who cannot open them.
        */}
        <button
          onClick={() => {
            void navigate("/admin");
          }}
        >
          Settings
        </button>

        <span className="small muted whoami">
          {principal?.type === "agent" ? `${principal.email} · ${principal.role}` : ""}
        </span>
        <button
          onClick={() => {
            void signOut();
          }}
        >
          Sign out
        </button>
      </header>

      {/*
        On a phone the two panes take turns rather than stacking — see the
        `.has-conversation` rule. Stacked, the conversation sat below a scrolling
        list of every conversation, so opening one appeared to do nothing at all.
      */}
      <div className={conversationId === undefined ? "workspace" : "workspace has-conversation"}>
        <QueueList
          conversations={conversations}
          loading={loading}
          error={error}
          filter={filter}
          onFilterChange={setFilter}
          unread={unread}
          selectable={can(PERMISSIONS.SUPPORT_CONVERSATIONS_DELETE)}
          checked={checked}
          deleting={deleting}
          onCheck={(id, on) => {
            setChecked((current) => {
              const next = new Set(current);
              if (on) next.add(id);
              else next.delete(id);
              return next;
            });
          }}
          onCheckAll={(on) => {
            setChecked(on ? new Set(conversations.map((c) => c.conversationId)) : new Set());
          }}
          onDeleteChecked={() => void deleteChecked()}
          selectedId={conversationId ?? null}
          onSelect={(id) => {
            void navigate(`/conversations/${id}`);
          }}
          onRefresh={() => void loadQueue()}
        />

        {conversationId === undefined ? (
          <div className="centered">
            <p className="muted">Select a conversation to begin.</p>
          </div>
        ) : (
          <ConversationView
            key={conversationId}
            conversationId={conversationId}
            realtime={client}
            revision={revision}
            typingEvent={typingEvent}
            presenceEvent={presenceEvent}
            onChanged={() => void loadQueue()}
            onBack={() => {
              // The queue's own route, rather than "/" and a redirect through it.
              void navigate("/conversations");
            }}
          />
        )}
      </div>
    </div>
  );
}
